import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { config } from '../config';
import * as fmt from '../format';
import { ROUTE_STROKE, loadMaps, metresToMiles } from '../maps';
import type { LatLng } from '../routeImport';
import {
  ImportError,
  decimate,
  looksLikeGoogleMapsLink,
  parseGoogleMapsDirections,
  parsePastedRoute,
  parseRouteFile,
  totalMetres,
} from '../routeImport';
import type { Route, RoutePoint, RouteRequest } from '../models';

/// Building and saving routes.
///
/// The route is drawn by Google's `DirectionsRenderer` in draggable mode,
/// which is what gives dragging the line to reshape it — that is a built-in of
/// the Maps API rather than anything implemented here. Every reshape fires
/// `directions_changed`, and the route's length is read back off the result,
/// so the saved distance always describes the line actually drawn.
///
/// Start and finish are plain text handed to the Directions service, which
/// geocodes them itself. Deliberately not Places Autocomplete: that widget is
/// mid-migration between the legacy and new Places APIs, and which one a key
/// can use depends on when the project was created. One less API to enable and
/// one less way for a fresh key to fail.

/// Google's ceiling for a *single* Directions request. It is not a ceiling on
/// how shaped a route can be: past this the route is split into several
/// requests and their geometry stitched back together.
const PER_REQUEST_WAYPOINTS = 25;

/// What a stitched route is allowed to reach. Each 25 costs one billed
/// Directions call, so this is a sanity bound rather than a technical one.
const MAX_WAYPOINTS = 250;

/// Dragging is a property of the DirectionsRenderer, and the renderer owns
/// exactly one result. A stitched route is several, and dragging one would
/// re-request it with every waypoint at once — straight back into the limit
/// the stitching exists to escape. So past this the line is drawn plainly and
/// shaped by clicking instead.
const DRAGGABLE_WAYPOINTS = PER_REQUEST_WAYPOINTS;

/// Matches the server's cap. An imported track is thinned until it fits rather
/// than rejected, because the alternative is telling someone their perfectly
/// good route is too detailed.
const MAX_POLYLINE = 200_000;

/// Thresholds tried in order until the encoded line fits. Zero first, so a
/// route small enough to keep every point keeps every point.
const THINNING_METRES = [0, 5, 10, 25, 50, 100, 250, 500, 1000];

interface Draft {
  distanceMiles: number;
  polyline: string;
  start: RoutePoint;
  finish: RoutePoint;
  waypoints: RoutePoint[];
  source: 'directions' | 'imported';
}

const asPoint = (value: google.maps.LatLng, label?: string): RoutePoint => ({
  lat: value.lat(),
  lng: value.lng(),
  ...(label === undefined ? {} : { label }),
});

/**
 * Pull a saveable route out of whatever the renderer is currently showing.
 *
 * Dragging the line does not create new legs — it adds `via_waypoints` inside
 * the leg you dragged — so the full ordered list is each leg's via points
 * followed by that leg's end, and the last leg's end is the finish rather than
 * a waypoint.
 */
function draftFrom(result: google.maps.DirectionsResult): Draft | null {
  const route = result.routes[0];
  if (!route) return null;
  const legs = route.legs;
  const first = legs[0];
  const last = legs[legs.length - 1];
  if (!first || !last) return null;

  const waypoints: RoutePoint[] = [];
  legs.forEach((leg, index) => {
    for (const via of leg.via_waypoints ?? []) waypoints.push(asPoint(via));
    if (index < legs.length - 1) waypoints.push(asPoint(leg.end_location));
  });

  const metres = legs.reduce((total, leg) => total + (leg.distance?.value ?? 0), 0);

  return {
    distanceMiles: Number(metresToMiles(metres).toFixed(2)),
    polyline: route.overview_polyline,
    start: asPoint(first.start_location, first.start_address),
    finish: asPoint(last.end_location, last.end_address),
    waypoints,
    source: 'directions',
  };
}

/**
 * Directions for a route with more waypoints than one request allows.
 *
 * Split into consecutive chunks, each asking for at most `PER_REQUEST_WAYPOINTS`
 * intermediate stops. Chunks share their boundary point — chunk two starts
 * where chunk one ended — because a gap of even one stop would draw a line
 * that jumps.
 *
 * Sequential rather than parallel: these are billed calls against a per-second
 * quota, and a route being built by hand is not waiting on throughput.
 */
async function stitch(
  service: google.maps.DirectionsService,
  travelMode: google.maps.TravelMode,
  stops: Array<string | google.maps.LatLng>,
  stopover = false,
  /// Waypoints per request. The API's ceiling is 25, but a long line can be
  /// refused well below it — see the fallback in `buildRoute`.
  chunkSize = PER_REQUEST_WAYPOINTS,
): Promise<{ path: google.maps.LatLng[]; metres: number; resolved: RoutePoint[] }> {
  const path: google.maps.LatLng[] = [];
  const resolved: RoutePoint[] = [];
  let metres = 0;
  let index = 0;

  while (index < stops.length - 1) {
    const end = Math.min(index + chunkSize + 1, stops.length - 1);
    const origin = stops[index];
    const destination = stops[end];
    if (!origin || !destination) break;

    const result = await service.route({
      origin,
      destination,
      travelMode,
      waypoints: stops.slice(index + 1, end).map((location) => ({ location, stopover })),
    });

    const route = result.routes[0];
    if (!route) throw new Error('Google returned no route for part of that line.');
    for (const leg of route.legs) metres += leg.distance?.value ?? 0;
    // The first point of each chunk repeats the last of the previous one.
    path.push(...(index === 0 ? route.overview_path : route.overview_path.slice(1)));

    // Where Google actually put each stop. Names go in; coordinates come back,
    // which is what makes a route built from a list editable later.
    route.legs.forEach((leg, legIndex) => {
      const isChunkEnd = legIndex === route.legs.length - 1;
      if (isChunkEnd && end === stops.length - 1) return; // the finish, not a waypoint
      resolved.push({ lat: leg.end_location.lat(), lng: leg.end_location.lng() });
    });
    for (const via of route.legs.flatMap((leg) => leg.via_waypoints ?? [])) {
      resolved.push({ lat: via.lat(), lng: via.lng() });
    }

    index = end;
  }

  return { path, metres, resolved };
}

/**
 * Which hops Google will not route, when it refuses the whole line.
 *
 * `ZERO_RESULTS` for a route with waypoints says nothing about *where* the
 * problem is, and a sixteen-point line gives no clue by inspection. Asking
 * for each consecutive pair on its own turns one useless answer into a
 * specific one: these two points, this gap.
 *
 * Only ever run after a failure, and one hop at a time, because it is a
 * request per pair.
 */
async function failingHops(
  service: google.maps.DirectionsService,
  travelMode: google.maps.TravelMode,
  points: google.maps.LatLng[],
): Promise<number[]> {
  const failed: number[] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const from = points[index];
    const to = points[index + 1];
    if (!from || !to) continue;
    try {
      await service.route({ origin: from, destination: to, travelMode });
    } catch {
      failed.push(index);
    }
  }
  return failed;
}

/**
 * The points to blame for a set of failing hops.
 *
 * A single bad point breaks the hop into it and the hop out of it, so a point
 * sitting between two consecutive failures is the culprit. A lone failing hop
 * blames its later point, which is the one that was added.
 */
function culprits(failed: number[], total: number): Set<number> {
  const failing = new Set(failed);
  const blamed = new Set<number>();
  for (const hop of failed) {
    // Hop n runs from point n to point n+1. When the hop before this one also
    // failed, the point they share is the one at fault. Otherwise blame the
    // far end, which is the point that was added.
    let suspect = failing.has(hop - 1) ? hop : hop + 1;
    // Never the endpoints: dropping the start or finish would quietly build a
    // different route rather than the one that was asked for. A hop failing at
    // either end blames its inward neighbour instead, which is the point that
    // can actually be removed.
    if (suspect >= total - 1) suspect = total - 2;
    if (suspect <= 0) suspect = 1;
    if (suspect > 0 && suspect < total - 1) blamed.add(suspect);
  }
  return blamed;
}

export function Routes() {
  const holder = useRef<HTMLDivElement | null>(null);
  const maps = useRef<typeof google.maps | null>(null);
  const map = useRef<google.maps.Map | null>(null);
  const service = useRef<google.maps.DirectionsService | null>(null);
  const renderer = useRef<google.maps.DirectionsRenderer | null>(null);
  const preview = useRef<google.maps.Polyline | null>(null);
  /// StrictMode mounts effects twice in development; the map must be built once.
  const started = useRef(false);

  const [ready, setReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);

  const [start, setStart] = useState('');
  const [finish, setFinish] = useState('');
  const [walking, setWalking] = useState(false);
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [waypoints, setWaypoints] = useState<RoutePoint[]>([]);
  const [pasted, setPasted] = useState('');
  const [stopList, setStopList] = useState('');
  const [editing, setEditing] = useState<Route | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [saved, setSaved] = useState<Route[]>([]);
  const [saving, setSaving] = useState(false);

  /// The map's click listener is attached once, when the map is built, so it
  /// would otherwise close over the state as it was then — empty. These are
  /// updated on every render and read at click time.
  const latest = useRef({ start: '', finish: '', waypoints: [] as RoutePoint[] });
  const rebuild = useRef<(
    from: string, to: string, via: RoutePoint[]
  ) => void>(() => {});

  const refreshSaved = useCallback(async () => {
    try {
      setSaved(await api.routes());
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const loaded = await loadMaps();
        maps.current = loaded;
        if (!holder.current) return;

        const created = new loaded.Map(holder.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        map.current = created;
        service.current = new loaded.DirectionsService();

        const drawn = new loaded.DirectionsRenderer({
          map: created,
          draggable: true,
          preserveViewport: false,
        });
        // The whole drag-to-reshape feature: the renderer mutates its own
        // directions and tells us, and the length comes back off the result.
        drawn.addListener('directions_changed', () => {
          const current = drawn.getDirections();
          if (current) setDraft(draftFrom(current));
        });
        renderer.current = drawn;

        // Clicking the map appends a waypoint. This is how a route is shaped
        // past 25, where dragging stops being available — and it is easier
        // than dragging even below it.
        created.addListener('click', (event: google.maps.MapMouseEvent) => {
          const at = event.latLng;
          const { start, finish, waypoints: current } = latest.current;
          if (!at || start.trim() === '' || finish.trim() === '') return;
          const next = [...current, { lat: at.lat(), lng: at.lng() }];
          setWaypoints(next);
          rebuild.current(start.trim(), finish.trim(), next);
        });

        setReady(true);
      } catch (failure) {
        setMapsError(failure instanceof Error ? failure.message : String(failure));
      }
    })();
  }, []);

  useEffect(() => { void refreshSaved(); }, [refreshSaved]);

  /// Ask for directions and draw them.
  ///
  /// Two paths, because Google allows 25 waypoints per request. Under that,
  /// the DirectionsRenderer owns the result and dragging works. Over it, the
  /// line is stitched from several requests and drawn plainly — the renderer
  /// could not own it, and dragging would re-request the whole thing at once
  /// and hit the very limit the stitching exists to escape.
  const buildRoute = useCallback(
    async (from: string | google.maps.LatLngLiteral,
           to: string | google.maps.LatLngLiteral,
           via: RoutePoint[],
           /// Passed in when it comes from a link, which carries its own mode
           /// and cannot wait for `setWalking` to take effect.
           onFoot?: boolean) => {
      const loaded = maps.current;
      if (!loaded || !service.current || !renderer.current || !map.current) return;
      if (via.length > MAX_WAYPOINTS) {
        setError(`That is ${via.length} waypoints; the most this will build is ${MAX_WAYPOINTS}.`);
        return;
      }

      setBusy(true);
      setError(null);
      setNotice(null);
      preview.current?.setMap(null);
      const walkingNow = onFoot ?? walking;
      const travelMode = walkingNow ? loaded.TravelMode.WALKING : loaded.TravelMode.DRIVING;
      const asLatLng = (point: string | google.maps.LatLngLiteral) =>
        typeof point === 'string' ? point : new loaded.LatLng(point.lat, point.lng);

      try {
        if (via.length <= DRAGGABLE_WAYPOINTS) {
          const result = await service.current.route({
            origin: from,
            destination: to,
            travelMode,
            waypoints: via.map((point) => ({
              location: new loaded.LatLng(point.lat, point.lng),
              stopover: false,
            })),
          });
          renderer.current.setMap(map.current);
          renderer.current.setDirections(result);
          const next = draftFrom(result);
          setDraft(next);
          if (next) setWaypoints(next.waypoints);
          return;
        }

        // Stitched. The renderer is detached: what it holds no longer
        // describes what is on screen, and leaving it visible would draw a
        // stale line underneath the real one.
        renderer.current.setMap(null);
        const stops: Array<string | google.maps.LatLng> = [
          asLatLng(from),
          ...via.map((point) => new loaded.LatLng(point.lat, point.lng)),
          asLatLng(to),
        ];
        const { path, metres } = await stitch(service.current, travelMode, stops);
        const first = path[0];
        const last = path[path.length - 1];
        if (!first || !last) throw new Error('That route came back empty.');

        preview.current = new loaded.Polyline({
          path, map: map.current, strokeColor: ROUTE_STROKE, strokeWeight: 4,
        });
        setDraft({
          distanceMiles: Number(metresToMiles(metres).toFixed(2)),
          polyline: loaded.geometry.encoding.encodePath(path),
          start: { lat: first.lat(), lng: first.lng() },
          finish: { lat: last.lat(), lng: last.lng() },
          waypoints: via,
          source: 'directions',
        });
      } catch (failure) {
        const message = failure instanceof Error ? failure.message : String(failure);
        if (!message.includes('ZERO_RESULTS')) {
          setError(message);
          return;
        }
        if (walkingNow) {
          setError(
            'No walking route exists between those points. Google will not route on foot '
            + 'across continental distances — switch to driving, which follows roads you '
            + 'can still walk in spirit.',
          );
          return;
        }
        if (via.length === 0 || !service.current) {
          setError('Google could not find a road route between those two places.');
          return;
        }

        // Find out where, and go again without the points to blame. One
        // unroutable point out of sixteen should cost that point, not the
        // whole import.
        setError(null);
        setNotice('Google refused that line. Checking which points it cannot reach…');
        const points = [asLatLng(from), ...via.map((v) => new loaded.LatLng(v.lat, v.lng)),
                        asLatLng(to)]
          .map((point) => (typeof point === 'string' ? null : point))
          .filter((point): point is google.maps.LatLng => point !== null);

        if (points.length !== via.length + 2) {
          setError('Google could not route that line, and the ends are place names rather '
                   + 'than coordinates, so I cannot narrow it down. Try plotting the ends '
                   + 'first, then importing.');
          return;
        }

        const failed = await failingHops(service.current, travelMode, points);
        const blamed = culprits(failed, points.length);
        // Every hop routes on its own but the line is refused as a whole. No
        // point is at fault; the request is simply more than Google will take
        // in one go, and its 25-waypoint ceiling is not the real limit for a
        // line this long. So ask for the hops it has already agreed to and
        // join them: one request per hop, which the checks above just proved
        // will each succeed.
        if (blamed.size === 0) {
          setNotice('Google will not take that line in one request. Building it a hop at a '
                    + 'time instead…');
          const joined = await stitch(service.current, travelMode, points, true, 0);
          const first = joined.path[0];
          const last = joined.path[joined.path.length - 1];
          if (!first || !last) {
            setError('Building it hop by hop produced nothing. Try fewer points.');
            return;
          }
          renderer.current?.setMap(null);
          preview.current?.setMap(null);
          preview.current = new loaded.Polyline({
            path: joined.path, map: map.current, strokeColor: ROUTE_STROKE, strokeWeight: 4,
          });
          const bounds = new loaded.LatLngBounds();
          for (const point of joined.path) bounds.extend(point);
          map.current.fitBounds(bounds);
          setDraft({
            distanceMiles: Number(metresToMiles(joined.metres).toFixed(2)),
            polyline: loaded.geometry.encoding.encodePath(joined.path),
            start: { lat: first.lat(), lng: first.lng() },
            finish: { lat: last.lat(), lng: last.lng() },
            waypoints: via,
            source: 'directions',
          });
          setNotice(`Built from ${points.length - 1} separate requests, so the line cannot be `
                    + 'dragged — click the map to add points instead.');
          return;
        }

        const kept = via.filter((_, index) => !blamed.has(index + 1));
        setNotice(
          `Dropped ${blamed.size} point${blamed.size === 1 ? '' : 's'} Google could not reach `
          + `by road, and rebuilt with the remaining ${kept.length}.`,
        );
        setWaypoints(kept);
        await buildRoute(from, to, kept, onFoot);
      } finally {
        setBusy(false);
      }
    },
    [walking],
  );

  latest.current = { start, finish, waypoints };
  rebuild.current = (from, to, via) => { void buildRoute(from, to, via); };

  const onPlot = () => {
    if (!start.trim() || !finish.trim()) return;
    setEditing(null);
    setWaypoints([]);
    void buildRoute(start.trim(), finish.trim(), []);
  };

  const replaceWaypoints = (next: RoutePoint[]) => {
    setWaypoints(next);
    if (start.trim() && finish.trim()) void buildRoute(start.trim(), finish.trim(), next);
  };

  /// Reopening a saved route re-requests directions from its endpoints, which
  /// is what makes it draggable again. Drawing the stored polyline would be
  /// cheaper but gives back a line nobody can edit.
  const onEdit = (route: Route) => {
    setEditing(route);
    setName(route.name);
    setStart(route.start.label ?? `${route.start.lat},${route.start.lng}`);
    setFinish(route.finish.label ?? `${route.finish.lat},${route.finish.lng}`);
    setWalking(route.travelMode === 'WALKING');
    setWaypoints(route.waypoints);
    void buildRoute(
      { lat: route.start.lat, lng: route.start.lng },
      { lat: route.finish.lat, lng: route.finish.lng },
      route.waypoints,
    );
  };

  /// Just the stored geometry, no Directions call and nothing billable.
  const onPreview = (route: Route) => {
    const loaded = maps.current;
    if (!loaded || !map.current) return;
    renderer.current?.setMap(null);
    renderer.current?.setMap(map.current);
    preview.current?.setMap(null);

    const path = loaded.geometry.encoding.decodePath(route.polyline);
    const line = new loaded.Polyline({
      path, map: map.current, strokeColor: ROUTE_STROKE, strokeWeight: 4,
    });
    preview.current = line;

    const bounds = new loaded.LatLngBounds();
    for (const point of path) bounds.extend(point);
    map.current.fitBounds(bounds);
  };

  /**
   * Import a route another site exported.
   *
   * The distance is measured from every point in the file, then the line is
   * thinned only for storage - so a thinned polyline never costs accuracy in
   * the figure the whole app is computed against.
   */
  const adoptPath = (path: LatLng[], suggestedName: string) => {
    const loaded = maps.current;
    if (!loaded || !map.current) return;
      const miles = metresToMiles(totalMetres(path));

      let polyline = '';
      let points = path;
      for (const threshold of THINNING_METRES) {
        points = decimate(path, threshold);
        polyline = loaded.geometry.encoding.encodePath(
          points.map((point) => new loaded.LatLng(point.lat, point.lng)),
        );
        if (polyline.length <= MAX_POLYLINE) break;
      }
      if (polyline.length > MAX_POLYLINE) {
        throw new ImportError('That route is too detailed to store, even thinned.');
      }

      const first = path[0];
      const last = path[path.length - 1];
      if (!first || !last) throw new ImportError('That file has no usable points.');

      // Detach the renderer: this is a fixed track, not a directions result,
      // so there is nothing draggable for it to own.
      renderer.current?.setMap(null);
      preview.current?.setMap(null);
      const line = new loaded.Polyline({
        path: points.map((point) => new loaded.LatLng(point.lat, point.lng)),
        map: map.current,
        strokeColor: ROUTE_STROKE,
        strokeWeight: 4,
      });
      preview.current = line;
      const bounds = new loaded.LatLngBounds();
      for (const point of points) bounds.extend(new loaded.LatLng(point.lat, point.lng));
      map.current.fitBounds(bounds);

      setEditing(null);
      if (name === '') setName(suggestedName);
      setDraft({
        distanceMiles: Number(miles.toFixed(2)),
        polyline,
        start: { lat: first.lat, lng: first.lng, label: 'Imported start' },
        finish: { lat: last.lat, lng: last.lng, label: 'Imported finish' },
        // Not derivable from a bare track: a file records where the line went,
        // never which points were the ones somebody chose.
        waypoints: [],
        source: 'imported',
      });
  };

  /// The two ways in share everything after the parse, so they share a body:
  /// only "what did you hand me" differs.
  const importFrom = (read: () => LatLng[] | Promise<LatLng[]>, suggestedName: string) =>
    void (async () => {
      setBusy(true);
      setError(null);
      try {
        adoptPath(await read(), suggestedName);
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : String(failure));
      } finally {
        setBusy(false);
      }
    })();

  /**
   * Build a route through a list of stops, one per line.
   *
   * Names rather than coordinates: Directions geocodes each stop itself, so a
   * historic highway can be described the way it is actually written down —
   * the towns it goes through — instead of hunting up latitudes. What comes
   * back includes where Google put each one, which is what keeps the saved
   * route editable afterwards.
   */
  const buildFromStops = async (
    lines: string[],
    options: { walking?: boolean; startLabel?: string; finishLabel?: string } = {},
  ) => {
    const loaded = maps.current;
    if (!loaded || !service.current || !map.current) return;
    if (lines.length - 2 > MAX_WAYPOINTS) {
      setError(`That is ${lines.length - 2} stops between the ends; the most is ${MAX_WAYPOINTS}.`);
      return;
    }

    // Passed in rather than read from state: a link carries its own mode, and
    // setWalking would not have taken effect by the time we route.
    const onFoot = options.walking ?? walking;
    const startName = options.startLabel ?? lines[0];
    const finishName = options.finishLabel ?? lines[lines.length - 1];

    setBusy(true);
    setError(null);
    preview.current?.setMap(null);
    // Always drawn plainly: a list of stops is a shape somebody has already
    // decided on, and it is usually past the point where dragging works.
    renderer.current?.setMap(null);
    try {
      const travelMode = onFoot ? loaded.TravelMode.WALKING : loaded.TravelMode.DRIVING;
      const { path, metres, resolved } = await stitch(service.current, travelMode, lines, true);
      const first = path[0];
      const last = path[path.length - 1];
      if (!first || !last) throw new Error('That list produced no route.');

      preview.current = new loaded.Polyline({
        path, map: map.current, strokeColor: ROUTE_STROKE, strokeWeight: 4,
      });
      const bounds = new loaded.LatLngBounds();
      for (const point of path) bounds.extend(point);
      map.current.fitBounds(bounds);

      setWaypoints(resolved);
      setDraft({
        distanceMiles: Number(metresToMiles(metres).toFixed(2)),
        polyline: loaded.geometry.encoding.encodePath(path),
        start: { lat: first.lat(), lng: first.lng(), ...(startName ? { label: startName } : {}) },
        finish: { lat: last.lat(), lng: last.lng(), ...(finishName ? { label: finishName } : {}) },
        waypoints: resolved,
        source: 'directions',
      });
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : String(failure);
      setError(
        message.includes('ZERO_RESULTS')
          ? 'Google could not route between some of those stops. On foot it refuses long '
            + 'distances outright — try driving. Otherwise one of the stops is somewhere it '
            + 'cannot reach by road; removing it usually fixes it.'
          : message,
      );
    } finally {
      setBusy(false);
    }
  };

  const onBuildStops = () => {
    const text = stopList.trim();

    // A Google Maps directions link. Rebuilt from the coordinates in its data
    // blob rather than the names in its path, because the blob also carries
    // the points dragged in between stops - and without those it is a
    // different road that happens to visit the same towns.
    if (looksLikeGoogleMapsLink(text)) {
      try {
        const link = parseGoogleMapsDirections(text);
        const origin = link.stops[0];
        const destination = link.stops[link.stops.length - 1];
        if (!origin || !destination) throw new ImportError('That link has no usable points.');

        setEditing(null);
        setWalking(link.travelMode === 'WALKING');
        const startName = link.names[0];
        const finishName = link.names[link.names.length - 1];
        if (startName) setStart(startName);
        if (finishName) setFinish(finishName);

        // Straight through `buildRoute` rather than the stops builder. The
        // link already gives exact coordinates, so there is nothing to geocode
        // - and these were via points in the original, not stops. Routing them
        // as stops asks Google to arrive at each one, which it will refuse for
        // a point that only ever meant "go this way".
        const via = link.stops.slice(1, -1);
        setWaypoints(via);
        void buildRoute(origin, destination, via, link.travelMode === 'WALKING');
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : String(failure));
      }
      return;
    }

    const lines = text.split('\n').map((line) => line.trim()).filter((line) => line !== '');
    const first = lines[0];
    const last = lines[lines.length - 1];
    if (lines.length < 2 || !first || !last) {
      setError('List at least a start and a finish, one per line.');
      return;
    }
    setEditing(null);
    setStart(first);
    setFinish(last);
    void buildFromStops(lines);
  };

  const onSave = async () => {
    if (!draft || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const request: RouteRequest = {
        name: name.trim(),
        distanceMiles: draft.distanceMiles,
        polyline: draft.polyline,
        start: draft.start,
        finish: draft.finish,
        waypoints: draft.waypoints,
        travelMode: walking ? 'WALKING' : 'DRIVING',
        source: draft.source,
      };
      if (editing) await api.updateRoute(editing.routeId, request);
      else await api.createRoute(request);
      setName('');
      setEditing(null);
      await refreshSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (route: Route) => {
    setError(null);
    try {
      await api.deleteRoute(route.routeId);
      if (editing?.routeId === route.routeId) setEditing(null);
      await refreshSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  };

  if (!config.mapsKey) {
    return (
      <section className="card card--wide">
        <h2>Routes</h2>
        <p className="muted">
          No Google Maps key is configured, so the map cannot load. Set
          {' '}<code>VITE_GOOGLE_MAPS_KEY</code> at build time.
        </p>
      </section>
    );
  }

  return (
    <section className="routes">
      <div className="card card--wide">
        <div className="card__head"><h2>{editing ? `Editing ${editing.name}` : 'Build a route'}</h2></div>

        <div className="fields routes__fields">
          <label>
            Start
            <input value={start} onChange={(event) => setStart(event.target.value)}
                   placeholder="San Francisco, CA" />
          </label>
          <label>
            Finish
            <input value={finish} onChange={(event) => setFinish(event.target.value)}
                   placeholder="New York, NY" />
          </label>
          <label className="routes__mode">
            <input type="checkbox" checked={walking}
                   onChange={(event) => setWalking(event.target.checked)} />
            Walking route
          </label>
          <button className="primary" onClick={onPlot} disabled={!ready || busy || !start || !finish}>
            {busy ? 'Plotting…' : 'Plot'}
          </button>
        </div>

        <p className="muted routes__hint">
          Drag the line to reshape it, or click the map to add a point. Up to
          {' '}{DRAGGABLE_WAYPOINTS} points can be dragged; beyond that the line is stitched from
          several requests and shaped by clicking.
        </p>

        <label className="routes__import">
          <span>Or import a route another site exported — GPX, KML or GeoJSON</span>
          <input
            type="file"
            accept=".gpx,.kml,.geojson,.json,application/gpx+xml,application/vnd.google-earth.kml+xml,application/geo+json"
            disabled={!ready || busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared so choosing the same file twice still fires a change.
              event.target.value = '';
              if (file) {
                importFrom(
                  async () => parseRouteFile(file.name, await file.text()),
                  file.name.replace(/\.[^.]+$/, ''),
                );
              }
            }}
          />
        </label>

        <details className="routes__paste">
          <summary>Or build from a list of stops, or a Google Maps link</summary>
          <p className="muted">
            Paste a Google Maps directions link and it is rebuilt exactly — including any points
            dragged in to shape it, which the stop names alone would lose. Or list stops one per
            line: first is the start, last is the finish, place names or <code>lat, lng</code>
            {' '}pairs both work. Long lists are split across several requests, so neither is
            limited to 25.
          </p>
          <textarea
            value={stopList}
            rows={5}
            spellCheck={false}
            placeholder={'https://www.google.com/maps/dir/…\n\nor\n\nChicago, IL\nSt Louis, MO\n…\nSanta Monica, CA'}
            onChange={(event) => setStopList(event.target.value)}
          />
          <button disabled={!ready || busy || stopList.trim() === ''} onClick={onBuildStops}>
            Build from this
          </button>
        </details>

        <details className="routes__paste">
          <summary>Or paste one — a polyline, a Directions response, or coordinates</summary>
          <p className="muted">
            Anything you can get out of another site: an encoded polyline, the JSON body of a
            Directions call from its network traffic, a GPX or GeoJSON blob, or a plain list of
            <code> lat, lng </code> pairs one per line.
          </p>
          <textarea
            value={pasted}
            rows={4}
            spellCheck={false}
            placeholder="_p~iF~ps|U_ulLnnqC_mqNvxq`@"
            onChange={(event) => setPasted(event.target.value)}
          />
          <button
            disabled={!ready || busy || pasted.trim() === ''}
            onClick={() => importFrom(() => parsePastedRoute(pasted), 'Pasted route')}
          >
            Import pasted
          </button>
        </details>

        {mapsError && <p className="error">{mapsError}</p>}
        {error && <p className="error">{error}</p>}
        {notice && <p className="muted">{notice}</p>}

        <div ref={holder} className="routes__map" />

        {draft && (
          <div className="fields routes__save">
            <p className="routes__summary">
              <strong>{fmt.miles(draft.distanceMiles)}</strong>
              <span className="muted">
                {draft.waypoints.length === 0
                  ? ' · no waypoints'
                  : ` · ${draft.waypoints.length} waypoint${draft.waypoints.length === 1 ? '' : 's'}`}
              </span>
            </p>
            {waypoints.length > 0 && (
              <div className="routes__waypoints">
                <button onClick={() => replaceWaypoints(waypoints.slice(0, -1))} disabled={busy}>
                  Undo last point
                </button>
                <button onClick={() => replaceWaypoints([])} disabled={busy}>Clear points</button>
                {waypoints.length > DRAGGABLE_WAYPOINTS && (
                  <span className="muted">
                    Past {DRAGGABLE_WAYPOINTS} points the line is built from several requests, so
                    it cannot be dragged — click the map to keep shaping it.
                  </span>
                )}
              </div>
            )}
            <label>
              Name
              <input value={name} onChange={(event) => setName(event.target.value)}
                     placeholder="Across America" />
            </label>
            <button className="primary" onClick={() => void onSave()}
                    disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : editing ? 'Update route' : 'Save route'}
            </button>
            {editing && (
              <button onClick={() => { setEditing(null); setName(''); }}>New instead</button>
            )}
          </div>
        )}
      </div>

      <div className="card card--wide">
        <div className="card__head"><h2>Saved routes</h2></div>
        {saved.length === 0 ? (
          <p className="muted">None yet. Plot one above and give it a name.</p>
        ) : (
          <ul className="routes__list">
            {saved.map((route) => (
              <li key={route.routeId}>
                <div>
                  <strong>{route.name}</strong>
                  <span className="muted">
                    {' '}{fmt.miles(route.distanceMiles)}
                    {route.source === 'imported'
                      ? ' · imported'
                      : route.travelMode === 'WALKING' ? ' · walking' : ''}
                  </span>
                  <p className="muted routes__ends">
                    {route.start.label ?? 'Start'} → {route.finish.label ?? 'Finish'}
                  </p>
                </div>
                <div className="routes__actions">
                  <button onClick={() => onPreview(route)}>Show</button>
                  <button
                    onClick={() => onEdit(route)}
                    disabled={route.source === 'imported'}
                    title={route.source === 'imported'
                      ? 'Imported routes are a fixed track — reshaping would replace the line '
                        + 'you imported with a different one.'
                      : undefined}
                  >
                    Edit
                  </button>
                  <button onClick={() => void onDelete(route)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
